<template>
    <v-container fluid>
        <v-card class="mb-4 overflow-visible">
            <v-card-title>Customer Rework Search</v-card-title>
            <v-card-text>
                <v-row align="start">
                    <v-col cols="12" sm="3">
                        <contact-search-input v-model="selectedContact" label="Filter by Customer..."></contact-search-input>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field v-model="searchText"
                                      label="Search description, job or customer..."
                                      hide-details
                                      clearable
                                      prepend-inner-icon="mdi-magnify"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-text-field v-model="startDate"
                                      label="Start Date"
                                      type="date"
                                      hide-details
                                      :max="endDate || undefined"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-text-field v-model="endDate"
                                      label="End Date"
                                      type="date"
                                      hide-details
                                      :min="startDate || undefined"
                                      :max="maxEndDate || undefined"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-select v-model="selectedLine"
                                  label="Line"
                                  hide-details
                                  clearable
                                  :items="$constants.jobs.lines"
                                  item-title="text"
                                  item-value="value"></v-select>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-select v-model="selectedReasons"
                                  label="Rework Reason"
                                  hide-details
                                  clearable
                                  multiple
                                  chips
                                  closable-chips
                                  :items="reasonOptions"
                                  item-title="reason"
                                  item-value="reason"></v-select>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-select v-model="selectedSubReasons"
                                  label="Rework Sub Reason"
                                  hide-details
                                  clearable
                                  multiple
                                  chips
                                  closable-chips
                                  :items="subReasonOptions"></v-select>
                    </v-col>
                </v-row>
                <v-alert v-if="dateError" type="warning" density="compact" class="mt-2" :text="dateError"></v-alert>
            </v-card-text>
        </v-card>

        <v-card v-if="results !== null || loading" class="position-static">
            <v-card-text>
                <v-skeleton-loader v-if="loading" type="table-tbody"></v-skeleton-loader>
                <div v-else-if="results !== null && results.length === 0" class="text-center py-4 text-medium-emphasis">
                    No rework results found for the selected filters.
                </div>
                <v-table v-else-if="results !== null && results.length > 0" density="compact">
                    <thead>
                        <tr>
                            <th class="text-left">Customer</th>
                            <th class="text-left">Job</th>
                            <th class="text-left">Date</th>
                            <th class="text-left">Description</th>
                            <th class="text-left">Colour</th>
                            <th class="text-left">Line</th>
                            <th class="text-left">Rework Reason</th>
                            <th class="text-left">Sub Reason</th>
                            <th class="text-left">Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in results" :key="index">
                            <td><span v-html="formatSearchName(item.customerName, searchText)"></span></td>
                            <td>
                                <router-link :to="'/jobs/' + item.jobId">
                                    <span v-html="formatSearchName(item.jobName, searchText)"></span>
                                </router-link>
                            </td>
                            <td>{{ item.targetStartDate ? $dayjs(item.targetStartDate).format('DD/MM/YYYY') : '' }}</td>
                            <td><span v-html="formatSearchName(item.jobItemDescription, searchText)"></span></td>
                            <td>{{ item.jobColour }}</td>
                            <td>{{ item.line }}</td>
                            <td>{{ item.reworkReason }}</td>
                            <td>{{ item.reworkSubReason }}</td>
                            <td><span v-html="formatSearchName(item.reason, searchText)"></span></td>
                        </tr>
                    </tbody>
                </v-table>
            </v-card-text>
        </v-card>

        <v-card v-if="results === null && !loading" class="text-center pa-6 text-medium-emphasis">
            Select a start and end date to search for reworks.
        </v-card>
    </v-container>
</template>
<style scoped>
    :deep(.highlight-text) {
        background-color: #FFF176;
        font-weight: bold;
    }
</style>
<script>
    import { mapActions } from 'vuex';
    import { formatSearchName } from '../mixins/searchMixins';
    function initialState() {
        return {
            loading: false,
            selectedContact: null,
            searchText: '',
            startDate: null,
            endDate: null,
            selectedLine: null,
            selectedReasons: [],
            selectedSubReasons: [],
            results: null,
            searchTimer: null
        };
    }
    export default {
        name: "ReworkSummaryView",
        data() {
            return initialState();
        },
        mounted() {
            const today = this.$dayjs();
            this.startDate = today.startOf('month').format('YYYY-MM-DD');
            this.endDate = today.format('YYYY-MM-DD');
        },
        methods: {
            ...mapActions(['showErrorSnack']),
            formatSearchName,
            fetchData() {
                if (this.dateError) return;
                this.loading = true;
                const params = new URLSearchParams();
                params.append('startDate', this.startDate);
                params.append('endDate', this.endDate);
                if (this.selectedContact) {
                    params.append('contactId', this.selectedContact.id);
                }
                if (this.searchText && this.searchText.trim().length > 0) {
                    params.append('searchTerm', this.searchText.trim());
                }
                if (this.selectedLine !== null && this.selectedLine !== undefined) {
                    params.append('lineFilter', this.selectedLine);
                }
                (this.selectedReasons || []).forEach(reason => params.append('reworkReasons', reason));
                (this.selectedSubReasons || []).forEach(subReason => params.append('reworkSubReasons', subReason));
                this.$api.get('report/reworksearch?' + params.toString())
                    .then(res => {
                        this.results = res.data;
                        this.loading = false;
                    })
                    .catch(() => {
                        this.showErrorSnack('There was an issue fetching rework data.');
                        this.loading = false;
                    });
            },
            scheduleSearch() {
                clearTimeout(this.searchTimer);
                this.searchTimer = setTimeout(this.fetchData, 300);
            }
        },
        computed: {
            reasonOptions() {
                return this.$constants.jobs.reworkReasons;
            },
            subReasonOptions() {
                // Limit sub reasons to the selected reasons (or all if none selected)
                const reasons = this.selectedReasons && this.selectedReasons.length > 0
                    ? this.reasonOptions.filter(a => this.selectedReasons.includes(a.reason))
                    : this.reasonOptions;
                return reasons.flatMap(a => a.subReasons);
            },
            maxEndDate() {
                if (this.startDate) {
                    return this.$dayjs(this.startDate).add(24, 'month').format('YYYY-MM-DD');
                }
                return null;
            },
            dateError() {
                if (!this.startDate || !this.endDate) {
                    return 'Start and end date are required.';
                }
                const diff = this.$dayjs(this.endDate).diff(this.$dayjs(this.startDate), 'month');
                if (diff > 24) {
                    return 'Date range cannot exceed 24 months.';
                }
                if (this.$dayjs(this.endDate).isBefore(this.$dayjs(this.startDate))) {
                    return 'End date must be after start date.';
                }
                return null;
            }
        },
        watch: {
            startDate() { this.scheduleSearch(); },
            endDate() { this.scheduleSearch(); },
            selectedContact() { this.scheduleSearch(); },
            searchText() { this.scheduleSearch(); },
            selectedLine() { this.scheduleSearch(); },
            selectedReasons() {
                // Drop any sub reasons that no longer belong to the selected reasons
                const valid = (this.selectedSubReasons || []).filter(a => this.subReasonOptions.includes(a));
                if (valid.length != (this.selectedSubReasons || []).length) {
                    this.selectedSubReasons = valid;
                }
                this.scheduleSearch();
            },
            selectedSubReasons() { this.scheduleSearch(); }
        }
    };
</script>
